/* MJ search: the header's dropdown panel (<ab-search>, snippets/ab-search.liquid)
   and the shared pill form (<ab-search-form>, snippets/ab-search-form.liquid).
   Live results come from the predictive search endpoint rendering
   sections/ab-search-results.liquid. Dawn's global.js supplies debounce and
   getFocusableElements. Styles: assets/ab-search.css. */

class AbSearchForm extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.clear = this.querySelector('[data-search-clear]');
    if (!this.input || !this.clear) return;

    this.input.addEventListener('input', () => this.syncClear());
    this.clear.addEventListener('click', () => {
      this.input.value = '';
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
      this.input.focus();
    });
    this.input.form.addEventListener('submit', (event) => {
      if (!this.input.value.trim()) event.preventDefault();
    });
    this.syncClear();
  }

  syncClear() {
    this.clear.hidden = this.input.value.length === 0;
  }
}

customElements.define('ab-search-form', AbSearchForm);

class AbSearch extends HTMLElement {
  constructor() {
    super();
    this.onKeydown = this.onKeydown.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.position = this.position.bind(this);
    this.cache = new Map();
    this.query = '';
  }

  connectedCallback() {
    this.panel = this.querySelector('.ab-search__panel');
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-search-results]');
    this.status = this.querySelector('[data-search-status]');
    // the server-rendered "before you type" content (live from the first open), restored when
    // the input empties
    this.idle = null;

    this.querySelector('[data-search-close]').addEventListener('click', () => this.close());
    this.input.addEventListener('input', debounce(() => this.onInput(), 250));
    // a touch on the results dismisses the on-screen keyboard; focus stays in the dialog
    this.results.addEventListener(
      'touchstart',
      () => {
        if (document.activeElement === this.input) this.panel.focus({ preventScroll: true });
      },
      { passive: true }
    );
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  toggle(trigger) {
    if (this.isOpen) {
      this.close();
    } else {
      this.open(trigger);
    }
  }

  open(trigger) {
    if (this.isOpen) return;
    this.trigger = trigger instanceof HTMLElement ? trigger : null;
    this.showIdle();
    this.position();
    this.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('resize', this.position);
    window.visualViewport?.addEventListener('resize', this.position);
    this.input.focus();
  }

  // The idle content ships in an inert <template> (snippets/ab-search.liquid) so the closed panel
  // requests none of its tile images; it goes live, in the same frame, on the first open.
  showIdle() {
    if (this.idle) return;
    const template = this.results.querySelector('template[data-search-idle]');
    if (!template) return;
    this.idle = template.content.firstElementChild;
    template.replaceWith(template.content);
  }

  close() {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('resize', this.position);
    window.visualViewport?.removeEventListener('resize', this.position);
    this.trigger?.focus();
  }

  // Escape closes; Tab cycles inside the dialog. The tabbables are read on
  // every Tab because the results re-render while typing.
  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault(); // WebKit/Chrome would also empty a type=search input
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const tabbables = getFocusableElements(this.panel).filter((element) => element.offsetParent !== null);
    const first = tabbables[0];
    const last = tabbables[tabbables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // A press anywhere outside the dialog (scrim, the header's other controls)
  // closes it; the button that opened it toggles via its own click handler.
  onPointerDown(event) {
    if (this.contains(event.target) || this.trigger?.contains(event.target)) return;
    this.close();
  }

  // The panel hangs from whichever header is on screen: the fixed sticky
  // clone once the page is scrolled, else the static header (which can sit
  // above the viewport on pages with the sticky header off, hence the clamp).
  // Its height follows the visual viewport so the on-screen keyboard never
  // hides the end of the results.
  position() {
    const sticky = document.querySelector('.ab-header-sticky.is-visible');
    const header = document.querySelector('.ab-header:not(.ab-header-sticky)');
    const top = sticky ? sticky.offsetHeight : Math.max(0, header ? header.getBoundingClientRect().bottom : 0);
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    this.style.setProperty('--ab-search-top', `${top}px`);
    this.style.setProperty('--ab-search-height', `${Math.max(0, viewportHeight - top)}px`);
  }

  onInput() {
    const query = this.input.value.trim();
    if (query === this.query) return;
    this.query = query;
    this.controller?.abort();

    if (!query) {
      this.render(this.idle, '');
      return;
    }

    const cached = this.cache.get(query);
    if (cached) {
      this.render(cached.cloneNode(true), cached.dataset.status);
      return;
    }
    this.fetchResults(query);
  }

  fetchResults(query) {
    this.controller = new AbortController();
    this.setAttribute('loading', '');
    this.status.textContent = this.dataset.loading;

    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,query,article',
      'resources[limit]': '6',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      section_id: 'ab-search-results',
    });

    fetch(`${this.dataset.url}?${params}`, { signal: this.controller.signal })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(response.status))))
      .then((html) => {
        const found = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-search-found]');
        if (!found) throw new Error('search results markup missing');
        this.cache.set(query, found);
        if (this.query === query) this.render(found.cloneNode(true), found.dataset.status);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.removeAttribute('loading');
        this.status.textContent = this.dataset.error;
      });
  }

  render(node, status) {
    this.removeAttribute('loading');
    // no results: keep the popular searches and product strip as the way onward
    if (node.hasAttribute('data-search-empty')) node.append(this.idle.cloneNode(true));
    this.results.replaceChildren(node);
    this.results.scrollTop = 0;
    this.status.textContent = status;
  }
}

customElements.define('ab-search', AbSearch);
